import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createStripeAccount, createAccountLink, updateAccountLink } from '../services/stripeService';

const STORE_API_URL = process.env.REACT_APP_API_URL;

function StripeSettings() {
  const { token } = useContext(AuthContext);
  const [settings, setSettings] = useState({
    stripeAccountId: '',
    stripeAccountCreatedAt: '',
    stripeOnboardingComplete: '',
    stripeOnboardingCompletedAt: '',
    stripeAccountDetailsSubmitted: '',
    stripeAccountPayoutsEnabled: ''
  });
  const [stripeOnboardloading, setStripeOnboardLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      fetchStripeSettings();
    }
  }, [token]);

  const fetchStripeSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${STORE_API_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch Stripe settings');
      const data = await response.json();
      const stripeAccount = data.stripe_accounts?.[0] || {};
      setSettings({
        stripeAccountId: stripeAccount.account_id || '',
        stripeAccountCreatedAt: stripeAccount.created_at || '',
        stripeOnboardingComplete: stripeAccount.onboarding_complete || false,
        stripeOnboardingCompletedAt: stripeAccount.onboarding_completed_at || '',
        stripeAccountDetailsSubmitted: stripeAccount.account?.details_submitted || false,
        stripeAccountPayoutsEnabled: stripeAccount.account?.payouts_enabled || false,
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStripeAccountOnboard = async () => {
    setStripeOnboardLoading(true);
    setError(null);

    try {
      // Step 1: Create Stripe account
      const accountId = await createStripeAccount(token);
      console.log('Stripe account created:', accountId);

      // Step 2: Create account link for onboarding
      const accountLink = await createAccountLink(token, accountId);
      console.log('Account link created:', accountLink);

      // Step 3: Redirect to the onboarding link
      window.location.href = accountLink.url;
    } catch (err) {
      setError(err.message);
    } finally {
      setStripeOnboardLoading(false);
    }
  };

  const handleStripeAccountUpdate = async () => {
    setStripeOnboardLoading(true);
    setError(null);

    try {
      // Step 1: Create account link for onboarding
      const accountLink = await updateAccountLink(token);

      // Step 2: Redirect to the account update link
      window.location.href = accountLink.url;
    } catch (err) {
      setError(err.message);
    } finally {
      setStripeOnboardLoading(false);
    }
  };

  const handleNavigateToSettings = () => {
    navigate('/settings');
  };

  return (
    <div className="container">
      <ToastContainer />
      <div className="headline d-flex justify-content-between align-items-center">
        <h2 className="underline">Stripe Settings</h2>
        {!settings.stripeOnboardingComplete && (
          <div className="form-group">
            <button className="btn btn-primary" onClick={handleStripeAccountOnboard} disabled={stripeOnboardloading}>
              {stripeOnboardloading ? 'Loading...' : 'Start Onboarding'}
            </button>
          </div>
        )}
      </div>
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <button className="btn btn-primary me-2 mb-2" onClick={handleNavigateToSettings}>Back to Settings</button>
        </div>
      </div>
      {!loading && !error && !settings.stripeOnboardingComplete && (
        <div className="col-md-12 mt-3">
          <div className="card mb-3 p-3" style={{ border: '2px solid #ff5722' }}>
            <div className="card-body">
              <h5 className="card-title" style={{ color: '#ff5722', fontWeight: 'bold' }}>Setup Stripe to start accepting online payments.</h5>
              <p>Onboarding is handled by Stripe. Your personal information stays with Stripe. Stripe will connect your account to the Sunthar.com account, so that 
                your users can pay for your products and services. Only information such as purchases, products, shipping rates etc. are sync'd from Stripe. Your personal information is never shared by Stripe. It will be between you and Stripe. 
                As a merchant you are more or less independent. You will be directly responsible for chargebacks and disputes. If you have any questions about this, please don't hesitate to reach out.</p>
              <button className="btn btn-primary" onClick={handleStripeAccountOnboard} disabled={stripeOnboardloading}>
                {stripeOnboardloading ? 'Loading...' : 'Start Onboarding'}
              </button>
            </div>
          </div>
        </div>
      )}
      {settings.stripeAccountId && (
      <div>
        <div className="form-group">
          <label>Stripe Account ID</label>
          <input
            type="text"
            className="form-control"
            value={settings.stripeAccountId ? settings.stripeAccountId : 'N/A'}
            readOnly
            disabled
          />
        </div>
        <div className="form-group">
          <label>Onboarded Date</label>
          <input
            type="text"
            className="form-control"
            value={settings.stripeOnboardingComplete ? new Date(settings.stripeOnboardingCompletedAt).toLocaleString() : 'Not complete'}
            readOnly
            disabled
          />
        </div>
        <div className="form-group">
          <label>Details submitted?</label>
          <input
            type="text"
            className="form-control"
            value={settings.stripeAccountDetailsSubmitted ? 'Submitted' : 'Not complete'}
            readOnly
            disabled
          />
        </div>
        <div className="form-group">
          <label>Payout Enabled?</label>
          <input
            type="text"
            className="form-control"
            value={settings.stripeAccountPayoutsEnabled? 'Yes' : 'No. Complete Stripe ID Verification'}
            readOnly
            disabled
          />
        </div>
        <div className="form-group">
          <button className="btn btn-primary" onClick={handleStripeAccountUpdate} disabled={stripeOnboardloading}>
            {stripeOnboardloading
              ? 'Loading...'
              : settings.stripeAccountPayoutsEnabled
              ? 'Update Account'
              : 'ID Verification'}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}

export default StripeSettings;
